// Personal libs
import { Repository } from './common/repository.class';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { ApiError } from '@shared/error/api.error';
import {
  apiDeleteVideo,
  apiPutValidateVideo,
  apiPutInvalidateVideo,
  IApiPutInvalidateVideo,
  apiPutAskToValidateVideo,
  apiPostVideo,
  IApiPostVideo,
} from '@data/datasource/remote/video.api';
import { removeVideoFromCache } from '@shared/lib/service-worker.lib';
import { ApiErrorLabel } from '@shared/enum/api-error-label.enum';

class VideoRepository extends Repository {
  constructor() {
    super();
  }

  /*****************************************
   *
   * PUT FUNCTIONS
   *
   *****************************************/
  // Validate the video
  validateVideo(
    videoId?: number,
  ): Promise<IApiResponse<string, string> | undefined> {
    return apiPutValidateVideo(videoId).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (err.code === 'VIDEO_VALIDATE_ERR_01') {
          throw new Error('Vidéo introuvable');
        } else if (err.code === 'VIDEO_VALIDATE_ERR_02') {
          throw new Error('Utilisateur introuvable');
        } else if (err.code === 'VIDEO_VALIDATE_ERR_03') {
          throw new Error('Action interdite avec les rôles que vous possédez');
        } else if (err.code === 'VIDEO_VALIDATE_ERR_04') {
          throw new Error('Erreur technique lors de la validation de la vidéo');
        }
      } else {
        throw new Error('Erreur inconnue');
      }
      return Promise.resolve(undefined);
    });
  }

  // Invalidate the video
  invalidateVideo(
    params?: IApiPutInvalidateVideo,
  ): Promise<IApiResponse<string, string> | undefined> {
    return apiPutInvalidateVideo({
      videoId: params?.videoId,
      invalidationReason: params?.invalidationReason,
    }).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (err.code === 'VIDEO_INVALIDATE_ERR_01') {
          throw new Error('Vidéo introuvable');
        } else if (err.code === 'VIDEO_INVALIDATE_ERR_02') {
          throw new Error('Utilisateur introuvable');
        } else if (err.code === 'VIDEO_INVALIDATE_ERR_03') {
          throw new Error('Action interdite avec les rôles que vous possédez');
        } else if (err.code === 'VIDEO_INVALIDATE_ERR_04') {
          throw new Error('Erreur technique lors de la validation de la vidéo');
        }
      } else {
        throw new Error('Erreur inconnue');
      }
      return Promise.resolve(undefined);
    });
  }

  // Invalidate the video
  askToValidateVideo(
    videoId?: number,
  ): Promise<IApiResponse<string, string> | undefined> {
    return apiPutAskToValidateVideo(videoId).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (err.code === 'VIDEO_PUBLISH_ERR_01') {
          throw new Error('Vidéo introuvable');
        } else if (err.code === 'VIDEO_PUBLISH_ERR_02') {
          throw new Error('Cette vidéo ne vous appartient pas.');
        } else if (err.code === 'VIDEO_PUBLISH_ERR_03') {
          throw new Error(
            "Action impossible pour une vidéo qui n'est pas en brouillon",
          );
        } else if (err.code === 'VIDEO_PUBLISH_ERR_04') {
          throw new Error(
            "Aucun utilisateur n'a le rôle nécessaire pour la validation de vidéos.",
          );
        }
      } else {
        throw new Error('Erreur inconnue');
      }
      return Promise.resolve(undefined);
    });
  }

  /*****************************************
   *
   * POST FUNCTIONS
   *
   *****************************************/
  // Post the video
  postVideo(params: IApiPostVideo): Promise<IApiResponse<string, string> | undefined> {
    return apiPostVideo(params)
      .catch((err: ErrorConstructor) => {
        if (err instanceof ApiError) {
          if (err.code === 'VIDEO_UPLOAD_ERR_01') {
            throw new Error('Mauvais paramètres');
          } else if (err.code === 'VIDEO_UPLOAD_ERR_02') {
            throw new Error('Programme inconnu');
          } else if (err.code === 'VIDEO_UPLOAD_ERR_03') {
            throw new Error('Utilisateur inconnu');
          } else if (err.code === 'VIDEO_UPLOAD_ERR_04') {
            throw new Error('Pratiquant inconnu');
          } else if (err.code === 'VIDEO_UPLOAD_ERR_05') {
            throw new Error('Accompagnants inconnus');
          } else if (err.code === 'VIDEO_UPLOAD_ERR_06') {
            throw new Error(
              'Erreur technique lors de la sauvegarde de la vidéo en base',
            );
          } else {
            throw err
          }
        } else {
          throw new Error('Erreur inconnue');
        }
      });
  }

  /*****************************************
   *
   * DELETE FUNCTIONS
   *
   *****************************************/
  // Delete the video
  deleteVideo(
    videoId?: number,
  ): Promise<IApiResponse<string, string> | undefined> {
    return apiDeleteVideo(videoId)
      .catch((err: ErrorConstructor) => {
        if (err instanceof ApiError) {
          if (err.code === 'VIDEO_DELETE_ERR_01') {
            throw new Error('Vidéo introuvable');
          } else if (err.code === 'VIDEO_DELETE_ERR_02') {
            throw new Error('Utilisateur introuvable');
          } else if (err.code === 'VIDEO_DELETE_ERR_03') {
            throw new Error(
              'Action interdite avec les rôles que vous possédez',
            );
          } else if (err.code === 'VIDEO_DELETE_ERR_04') {
            throw new Error(
              'Erreur technique lors de la suppression de la vidéo',
            );
          }
        } else {
          throw new Error('Erreur inconnue');
        }
        return Promise.resolve(undefined);
      })
      .finally(() => {
        if (videoId != null) {
          removeVideoFromCache([videoId]);
        }
      });
  }
}

export default new VideoRepository();
